import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Lock, Mail, Phone, Calendar, User, MessageSquare, RefreshCw, LogOut, Inbox, FileText, Settings, Users, Search, Gift } from "lucide-react";
import axios from "axios";
import BlogManager from "@/components/admin/BlogManager";
import SiteSettings from "@/components/admin/SiteSettings";
import LeadMagnetSignups from "@/components/admin/LeadMagnetSignups";
import PageSeoManager from "@/components/admin/PageSeoManager";
import { WordOfMouthDashboard } from "@/components/admin/WordOfMouthDashboard";
import SEO from "@/components/SEO";
import { API } from "@/lib/api";

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [activeTab, setActiveTab] = useState("submissions"); // "submissions" | "blog" | "signups" | "pages-seo" | "settings"

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        fetchSubmissions();
      } else {
        setError("Incorrect password");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setSubmissions([]);
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      // Already sorted by backend (newest first)
      setSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInquiryBadgeColor = (type) => {
    const colors = {
      consultation: "bg-green-100 text-green-800",
      depression: "bg-blue-100 text-blue-800",
      anxiety: "bg-purple-100 text-purple-800",
      pain: "bg-orange-100 text-orange-800",
      general: "bg-gray-100 text-gray-800",
      other: "bg-yellow-100 text-yellow-800",
    };
    return colors[type] || colors.general;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="w-full max-w-md p-8 bg-white rounded-2xl shadow-soft">
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <h1 className="font-serif text-2xl font-semibold text-foreground">Admin Access</h1>
              <p className="text-muted-foreground mt-2">Enter password to view submissions</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12 rounded-xl"
                  data-testid="admin-password-input"
                />
              </div>
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
              <Button 
                type="submit" 
                className="w-full rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="admin-login-btn"
              >
                Access Dashboard
              </Button>
            </form>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30 pt-20" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" pathname="/admin" noIndex />
      {/* Header */}
      <div className="bg-white border-b border-border/40 py-6 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-foreground">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              {activeTab === "submissions" && `${submissions.length} total submission${submissions.length !== 1 ? "s" : ""}`}
              {activeTab === "blog" && "Manage blog posts & SEO"}
              {activeTab === "signups" && "Email list from lead magnet signups"}
              {activeTab === "pages-seo" && "Per-page focus keyword, meta description & image alt"}
              {activeTab === "settings" && "Code injection & integrations"}
              {activeTab === "word-of-mouth" && "Guide gifts, friend invites & follow-ups"}
            </p>
          </div>
          <div className="flex gap-3">
            {activeTab === "submissions" && (
              <Button
                variant="outline"
                onClick={fetchSubmissions}
                disabled={loading}
                className="rounded-full"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="rounded-full text-muted-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto mt-4 flex gap-2 border-b border-border/40 -mb-6">
          <button
            onClick={() => setActiveTab("submissions")}
            data-testid="tab-submissions"
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "submissions"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Inbox className="h-4 w-4" /> Contact Submissions
          </button>
          <button
            onClick={() => setActiveTab("blog")}
            data-testid="tab-blog"
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "blog"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <FileText className="h-4 w-4" /> Blog Posts
          </button>
          <button
            onClick={() => setActiveTab("signups")}
            data-testid="tab-signups"
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "signups"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users className="h-4 w-4" /> Email List
          </button>
          <button
            onClick={() => setActiveTab("word-of-mouth")}
            data-testid="tab-word-of-mouth"
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "word-of-mouth"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Gift className="h-4 w-4" /> Word of Mouth
          </button>
          <button
            onClick={() => setActiveTab("pages-seo")}
            data-testid="tab-pages-seo"
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "pages-seo"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Search className="h-4 w-4" /> Pages SEO
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            data-testid="tab-settings"
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "settings"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Settings className="h-4 w-4" /> Site Settings
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {activeTab === "blog" && <BlogManager />}
        {activeTab === "signups" && <LeadMagnetSignups adminPassword={password} />}
        {activeTab === "pages-seo" && <PageSeoManager adminPassword={password} />}
        {activeTab === "settings" && <SiteSettings adminPassword={password} />}
        {activeTab === "word-of-mouth" && <WordOfMouthDashboard adminPassword={password} />}
        {activeTab === "submissions" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Submissions Table */}
          <div className="lg:col-span-2">
            <Card className="bg-white rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No submissions yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    submissions.map((submission) => (
                      <TableRow 
                        key={submission.id}
                        className={`cursor-pointer hover:bg-secondary/50 ${selectedSubmission?.id === submission.id ? 'bg-secondary' : ''}`}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">{submission.name}</p>
                            <p className="text-sm text-muted-foreground">{submission.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getInquiryBadgeColor(submission.inquiry_type)} border-0`}>
                            {submission.inquiry_type.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(submission.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="text-primary">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedSubmission ? (
              <motion.div
                key={selectedSubmission.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-white rounded-2xl p-6 sticky top-24">
                  <h2 className="font-serif text-xl font-semibold text-foreground mb-4">
                    Submission Details
                  </h2>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Name</p>
                        <p className="font-medium text-foreground">{selectedSubmission.name}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <a 
                          href={`mailto:${selectedSubmission.email}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {selectedSubmission.email}
                        </a>
                      </div>
                    </div>

                    {selectedSubmission.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <a 
                            href={`tel:${selectedSubmission.phone}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {selectedSubmission.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Submitted</p>
                        <p className="font-medium text-foreground">
                          {formatDate(selectedSubmission.timestamp)}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border/40">
                      <div className="flex items-start gap-3">
                        <MessageSquare className="h-5 w-5 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Message</p>
                          <p className="text-foreground whitespace-pre-wrap">
                            {selectedSubmission.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 flex gap-2">
                      <Button 
                        className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={() => window.location.href = `mailto:${selectedSubmission.email}`}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Reply
                      </Button>
                      {selectedSubmission.phone && (
                        <Button 
                          variant="outline"
                          className="flex-1 rounded-full"
                          onClick={() => window.location.href = `tel:${selectedSubmission.phone}`}
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          Call
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ) : (
              <Card className="bg-white rounded-2xl p-6 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Select a submission to view details
                </p>
              </Card>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
